<template>
  <div>
    <form @submit.prevent="handleSubmit">
      <div id="payment-element"></div>
      <div class="button-group">
        <el-button
          type="primary"
          :disabled="isProcessing"
          @click="handleSubmit"
        >
          <span v-if="!isProcessing">Pay {{ amount }}</span>
          <span v-else>
            <ion-spinner name="dots" color="light"></ion-spinner
          ></span>
        </el-button>
      </div>
    </form>
  </div>
</template>

<script>
import { ref, onMounted, computed, reactive } from "vue";
import { loadStripe } from "@stripe/stripe-js";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { ElMessage } from "@/components/Vendor/Element";
import { IonSpinner } from "@ionic/vue";
export default {
  name: "StripePaymentForm",
  components: {
    IonSpinner,
  },
  props: {
    jobPostingPrice: {
      type: Number,
      required: true,
    },
    justJobPostingPrice: {
      type: Number,
      required: true,
    },
    jobId: {
      // indicates this is a job post update (publish)
      type: String,
      default: null,
    },
  },
  setup(props) {
    console.log("🚀 ~ setup ~ props:", props);
    const stripe = ref(null);
    const elements = ref(null);
    const paymentElement = ref(null);
    const isProcessing = ref(false);
    const $router = useRouter();
    const $store = useStore();
    const stripePublicKey = process.env.VUE_APP_STRIPE_PUBLIC_KEY;
    // const currentUrl = process.env.VUE_APP_CURRENT_URL;
    // const returnUrl = currentUrl + "create-job-listing/job-listing-payment-completed";
    const jobPostingPriceInCents = computed(() => props.jobPostingPrice * 100);
    const justJobPostingPriceInCents = computed(
      () => props.justJobPostingPrice * 100
    );
    const jobPostPaymentId = ref(null);
    const paymentIntentId = ref(null);
    const stripeCustomerId = ref(null);
    const amountToPay = ref(null);

    const form = reactive({
      job_post_payment_id: null,
      payment_intent_id: null,
      stripe_customer_id: null,
    });
    onMounted(async () => {
      try {
        stripe.value = await loadStripe(stripePublicKey);
        if (!stripe.value) {
          throw new Error("Stripe failed to load");
        }

        const {
          clientSecret,
          jobPostPaymentId: id,
          paymentIntentId: pid,
          stripeCustomerId: sid,
          amountDue,
        } = await createPaymentIntent();

        amountToPay.value = amountDue;
        form.job_post_payment_id = id;
        form.payment_intent_id = pid;
        form.stripe_customer_id = sid;

        // If amount to pay is 0, then no need to show payment form and just save the job post
        if (amountDue <= 0) {
          await saveJobPost();
        } else {
          elements.value = stripe.value.elements({ clientSecret });
          paymentElement.value = elements.value.create("payment", {
            // Add your options here
          });
          paymentElement.value.mount("#payment-element");
        }
      } catch (error) {
        console.error("Error setting up Stripe Elements:", error);
      }
    });

    const createPaymentIntent = async () => {
      try {
        const response = await $store.dispatch("CREATE_PAYMENT_INTENT", {
          amount: jobPostingPriceInCents.value, // amount in cents
          justAmount: justJobPostingPriceInCents.value, // amount in cents
          coupon: $store.getters.appliedCoupon
            ? $store.getters.appliedCoupon.id
            : null,
        });
        return response;
      } catch (error) {
        $store.dispatch("CLEAR_APPLIED_COUPON");
        $router.push({ name: "job_question_listing" });
      }
    };

    const handleSubmit = async () => {
      isProcessing.value = true;
      const url = getRouteUrl("job_poster");
      console.log("🚀 ~ handleSubmit ~ url:", url);
      try {
        const { error, paymentIntent } = await stripe.value.confirmPayment({
          elements: elements.value,
          confirmParams: {
            // return_url: returnUrl
          },
          redirect: "if_required",
        });

        if (error) {
          ElMessage.error(error.message);
        } else {
          // Handle successful payment here
          if (paymentIntent.status === "succeeded") {
            await saveJobPost();
          } else {
            console.warn(
              "PaymentIntent did not succeed:",
              paymentIntent.status
            );
          }
        }
      } catch (error) {
        console.error("Error handling payment submission:", error);
      } finally {
        isProcessing.value = false;
      }
    };

    const saveJobPost = async () => {
      try {
        $store.dispatch("setJobListingCreatePageData", form);
        if (props.jobId) {
          await $store.dispatch("UPDATE_JOB_POST_WITH_PAYMENT", {
            jobId: props.jobId,
            params: form,
          });
        } else {
          await $store.dispatch("SAVE_JOB_POST", "publish");
        }
        $store.dispatch("clearJobListingCreatePageData");
        $store.dispatch("CLEAR_APPLIED_COUPON");
        console.log("Job post saved and data cleared.");
        $router.push({
          name: "job_lisiting_payment_completed",
        });
      } catch (error) {
        console.error("Error saving job post:", error);
      }
    };

    const getRouteUrl = (routeName) => {
      const route = $router.resolve({ name: routeName });
      return route.href;
    };

    const amount = computed(() => {
      let amount = props.jobPostingPrice;
      if (amountToPay && amountToPay.value) {
        amount = amountToPay.value / 100;
      }

      console.log("🚀 ~ amount ~ amount:", amountToPay.value);
      return Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: "AUD",
      }).format(amount);
    });

    return {
      handleSubmit,
      isProcessing,
      createPaymentIntent,
      // returnUrl,
      jobPostingPriceInCents,
      justJobPostingPriceInCents,
      jobPostPaymentId,
      paymentIntentId,
      stripeCustomerId,
      amount,
      form,
    };
  },
};
</script>

<style>
#payment-element {
  margin-bottom: 16px;
}

button[disabled] {
  background: #ccc;
}
</style>
